import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Upload, ClipboardPaste, FileText, Camera, Check, AlertTriangle, ChevronRight, Sparkles } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { matchAccounts, matchOne, buildAccountMatcher, rerankAmbiguous } from './matchAccounts';
import { parsePastedText, parseSpreadsheetFile } from './parseImportInput';
import logger from '../../lib/logger';

const MODES = [
  { id: 'paste',       label: 'Paste text',     icon: ClipboardPaste, available: true },
  { id: 'spreadsheet', label: 'Spreadsheet',    icon: Upload,         available: true },
  { id: 'document',    label: 'PDF / DOCX',     icon: FileText,       available: true, ai: true },
  { id: 'photo',       label: 'Photo / Camera', icon: Camera,         available: true, ai: true },
];

const PASTE_PLACEHOLDER = `Paste one account per line, e.g.

California Department of Technology
California Department of Finance, T5 - GovOps
Franchise Tax Board

Optionally include a second column (tab or comma) for Territory.`;

export default function ImportFlowModal({ listId, existingMembers = [], onClose, onSaved }) {
  const [step, setStep] = useState('input'); // 'input' | 'matching' | 'review' | 'saving'
  const [mode, setMode] = useState('paste');
  const [pasted, setPasted] = useState('');
  const [fileEntries, setFileEntries] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [reviewRows, setReviewRows] = useState([]);
  const [mergeStrategy, setMergeStrategy] = useState('append'); // 'append' | 'replace'
  const [saveError, setSaveError] = useState(null);
  const [rerankProgress, setRerankProgress] = useState({ done: 0, total: 0 });

  // Full department rows for matching (richer than /api/coverage/departments —
  // includes alternative_names, abbreviations, etc., which the Fuse matcher
  // needs to nail aliases like "FI$Cal" or "CA ARNG").
  const departments = useQuery({
    queryKey: ['departments', 'all-for-matching'],
    queryFn: () => apiClient.get('/api/departments'),
    staleTime: 5 * 60 * 1000,
  });

  const fuse = useMemo(
    () => departments.data ? buildAccountMatcher(departments.data) : null,
    [departments.data]
  );

  const entries = useMemo(() => {
    if (mode === 'paste') return parsePastedText(pasted);
    if (mode === 'spreadsheet') return fileEntries || [];
    if (mode === 'document' || mode === 'photo') return fileEntries || [];
    return [];
  }, [mode, pasted, fileEntries]);

  const handleSpreadsheet = async (file) => {
    setFileError(null);
    try {
      const rows = await parseSpreadsheetFile(file);
      logger.accountLists.info('spreadsheet parsed', {
        filename: file.name,
        size: file.size,
        rowCount: rows.length,
      });
      if (rows.length === 0) {
        setFileError('No usable rows found. Expected a column like "Account Name" or "Account".');
        setFileEntries([]);
      } else {
        setFileEntries(rows);
      }
    } catch (e) {
      logger.accountLists.error('spreadsheet parse failed', { filename: file.name }, e);
      setFileError(e.message || 'Failed to parse file.');
      setFileEntries([]);
    }
  };

  // AI-extraction modes: image / PDF / DOCX. Backend routes by MIME and
  // returns either {mode:'ai', entries:[]} (image+PDF via Claude) or
  // {mode:'text', source_text} (DOCX via mammoth — we feed text through
  // the paste-parser).
  const handleAiUpload = async (file) => {
    setFileError(null);
    setAiBusy(true);
    setFileEntries(null);
    const t0 = performance.now();
    logger.accountLists.info('ai extract upload started', {
      filename: file.name,
      mime: file.type,
      size: file.size,
      mode,
    });
    try {
      const form = new FormData();
      form.append('file', file);
      // apiClient.post auto-handles FormData: skips Content-Type so the
      // browser sets multipart boundary, and still attaches the auth token.
      const data = await apiClient.post('/api/account-lists/extract', form);
      if (data.mode === 'ai') {
        const found = Array.isArray(data.entries) ? data.entries.length : 0;
        logger.accountLists.info('ai extract returned', {
          mode: 'ai',
          entryCount: found,
          durationMs: Math.round(performance.now() - t0),
        });
        if (found === 0) {
          setFileError('No accounts found in this document.');
          setFileEntries([]);
        } else {
          setFileEntries(data.entries);
        }
      } else if (data.mode === 'text') {
        const parsed = parsePastedText(data.source_text || '');
        logger.accountLists.info('docx text extracted', {
          mode: 'text',
          sourceTextLength: (data.source_text || '').length,
          parsedRows: parsed.length,
          durationMs: Math.round(performance.now() - t0),
        });
        if (parsed.length === 0) {
          setFileError('Extracted text contained no recognizable accounts. Try copying its contents into the Paste tab.');
          setFileEntries([]);
        } else {
          setFileEntries(parsed);
        }
      } else {
        logger.accountLists.warn('ai extract: unexpected response shape', { keys: Object.keys(data || {}) });
        setFileError('Unexpected server response.');
        setFileEntries([]);
      }
    } catch (e) {
      logger.accountLists.error('ai extract upload failed', { filename: file.name, mode }, e);
      setFileError(e.message || 'Upload failed.');
      setFileEntries([]);
    } finally {
      setAiBusy(false);
    }
  };

  const runMatch = async () => {
    if (!departments.data || entries.length === 0) return;
    const fuzzyRows = matchAccounts(departments.data, entries);

    // Count how many rows actually need a rerank — display only.
    const ambiguous = fuzzyRows.filter(r => {
      const c = r.candidates?.[0];
      return c && c.confidence >= 0.40 && c.confidence < 0.85;
    }).length;

    if (ambiguous === 0) {
      setReviewRows(fuzzyRows);
      setStep('review');
      return;
    }

    setRerankProgress({ done: 0, total: ambiguous });
    setStep('matching');
    const reranked = await rerankAmbiguous(fuzzyRows, {
      concurrency: 4,
      onProgress: (done, total) => setRerankProgress({ done, total }),
    });
    setReviewRows(reranked);
    setStep('review');
  };

  const handleSave = async () => {
    setStep('saving');
    setSaveError(null);
    const t0 = performance.now();

    const newMembers = reviewRows.map(r => ({
      department_id: r.selected_department_id,
      salesforce_account_name: r.salesforce_account_name,
      salesforce_territory: r.salesforce_territory,
      match_confidence: r.match_confidence,
      match_source: r.match_source,
    }));

    let payload;
    if (mergeStrategy === 'replace') {
      payload = newMembers;
    } else {
      // Append — keep existing, add new ones not already present by salesforce_account_name.
      const existing = existingMembers.map(m => ({
        department_id: m.department_id,
        salesforce_account_name: m.salesforce_account_name,
        salesforce_territory: m.salesforce_territory,
        match_confidence: m.match_confidence,
        match_source: m.match_source,
      }));
      const seen = new Set(existing.map(e => e.salesforce_account_name));
      const toAdd = newMembers.filter(n => !seen.has(n.salesforce_account_name));
      payload = [...existing, ...toAdd];
    }

    // Deduplicate by department_id — keep first occurrence only, skip duplicates.
    // Unmatched rows (department_id=null) are kept as-is.
    const seenDeptIds = new Set();
    payload = payload.filter(m => {
      if (m.department_id == null) return true; // Keep unmatched
      if (seenDeptIds.has(m.department_id)) {
        logger.accountLists.info('dedup:skipped-duplicate-dept', {
          department_id: m.department_id,
          salesforce_account_name: m.salesforce_account_name,
        });
        return false; // Skip duplicate
      }
      seenDeptIds.add(m.department_id);
      return true;
    });

    try {
      await apiClient.put(`/api/account-lists/${listId}/members`, { members: payload });
      logger.accountLists.info('members saved', {
        listId,
        mergeStrategy,
        newRows: newMembers.length,
        totalRows: payload.length,
        existingRows: existingMembers.length,
        durationMs: Math.round(performance.now() - t0),
      });
      onSaved?.();
    } catch (e) {
      logger.accountLists.error('save members failed', { listId, mergeStrategy, payloadSize: payload.length }, e);
      setSaveError(e.message || 'Save failed');
      setStep('review');
    }
  };

  return (
    <div className="al-modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose?.();
    }}>
      <div className="al-modal" role="document">
        <header className="al-modal-header">
          <h2>Import accounts</h2>
          <button className="al-icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {step === 'input' && (
          <InputStep
            mode={mode} setMode={setMode}
            pasted={pasted} setPasted={setPasted}
            entries={entries}
            fileEntries={fileEntries}
            handleSpreadsheet={handleSpreadsheet}
            handleAiUpload={handleAiUpload}
            aiBusy={aiBusy}
            fileError={fileError}
            departmentsLoading={departments.isLoading}
            departmentsError={departments.error}
            onNext={runMatch}
          />
        )}

        {step === 'matching' && (
          <div className="al-modal-body al-saving">
            <div className="al-spinner" />
            <div>
              <div>Reranking ambiguous matches with embeddings…</div>
              {rerankProgress.total > 0 && (
                <div className="al-muted" style={{ marginTop: 4 }}>
                  {rerankProgress.done} / {rerankProgress.total}
                </div>
              )}
            </div>
          </div>
        )}

        {step === 'review' && (
          <ReviewStep
            rows={reviewRows} setRows={setReviewRows}
            fuse={fuse}
            departments={departments.data || []}
            mergeStrategy={mergeStrategy} setMergeStrategy={setMergeStrategy}
            existingCount={existingMembers.length}
            existingDeptIds={existingMembers.map(m => m.department_id).filter(Boolean)}
            saveError={saveError}
            onBack={() => setStep('input')}
            onSave={handleSave}
          />
        )}

        {step === 'saving' && (
          <div className="al-modal-body al-saving">
            <div className="al-spinner" />
            <div>Saving…</div>
          </div>
        )}
      </div>
    </div>
  );
}

function InputStep({
  mode, setMode, pasted, setPasted, entries, fileEntries,
  handleSpreadsheet, handleAiUpload, aiBusy,
  fileError, departmentsLoading, departmentsError, onNext,
}) {
  return (
    <>
      <div className="al-modal-body">
        <div className="al-mode-tabs" role="tablist">
          {MODES.map(m => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                role="tab"
                aria-selected={active}
                className={`al-mode-tab ${active ? 'is-active' : ''} ${!m.available ? 'is-disabled' : ''}`}
                disabled={!m.available}
                onClick={() => m.available && setMode(m.id)}
                title={m.available ? m.label : `${m.label} — ${m.comingSoon}`}
              >
                <Icon size={16} />
                <span>{m.label}</span>
                {!m.available && <span className="al-tab-badge">soon</span>}
              </button>
            );
          })}
        </div>

        {mode === 'paste' && (
          <div className="al-paste-pane">
            <textarea
              className="al-paste-input"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder={PASTE_PLACEHOLDER}
              rows={12}
            />
            <div className="al-paste-meta">
              {entries.length === 0
                ? <span className="al-muted">Paste accounts above to continue</span>
                : <span>{entries.length} entr{entries.length === 1 ? 'y' : 'ies'} detected</span>}
            </div>
          </div>
        )}

        {mode === 'spreadsheet' && (
          <div className="al-file-pane">
            <label className="al-file-drop">
              <Upload size={28} />
              <div className="al-file-drop-title">Choose a spreadsheet</div>
              <div className="al-file-drop-hint">.xlsx, .xls or .csv — first column should be account name; optional Territory column</div>
              <input
                type="file"
                accept=".csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={(e) => e.target.files?.[0] && handleSpreadsheet(e.target.files[0])}
              />
            </label>
            {fileError && <div className="al-file-error"><AlertTriangle size={14} /> {fileError}</div>}
            {fileEntries && fileEntries.length > 0 && (
              <div className="al-file-preview">
                <div className="al-file-preview-title">{fileEntries.length} accounts found — preview</div>
                <ul>
                  {fileEntries.slice(0, 5).map((e, i) => (
                    <li key={i}><span>{e.name}</span>{e.territory && <span className="al-muted"> · {e.territory}</span>}</li>
                  ))}
                  {fileEntries.length > 5 && <li className="al-muted">…and {fileEntries.length - 5} more</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        {mode === 'document' && (
          <AiUploadPane
            title="PDF or Word document"
            hint=".pdf or .docx — PDFs go through Claude Vision; .docx text is extracted and matched"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onFile={handleAiUpload}
            busy={aiBusy}
            fileEntries={fileEntries}
            fileError={fileError}
          />
        )}

        {mode === 'photo' && (
          <AiUploadPane
            title="Photo or live camera"
            hint="On mobile this opens the camera. On desktop, choose a photo or screenshot. Claude Vision extracts names."
            accept="image/png,image/jpeg,image/webp,image/gif"
            capture="environment"
            onFile={handleAiUpload}
            busy={aiBusy}
            fileEntries={fileEntries}
            fileError={fileError}
          />
        )}
      </div>

      <footer className="al-modal-footer">
        {departmentsError && <span className="al-error">Couldn't load departments — matching will fail.</span>}
        <div className="al-spacer" />
        <button
          className="al-btn al-btn-primary"
          onClick={onNext}
          disabled={departmentsLoading || entries.length === 0 || !!departmentsError}
        >
          Match {entries.length || ''} accounts <ChevronRight size={16} />
        </button>
      </footer>
    </>
  );
}

function ReviewStep({
  rows, setRows, fuse, departments, mergeStrategy, setMergeStrategy,
  existingCount, existingDeptIds = [], saveError, onBack, onSave,
}) {
  const counts = useMemo(() => {
    const auto = rows.filter(r => r.match_source === 'auto').length;
    const needs = rows.filter(r => r.match_source === 'needs_confirmation').length;
    const unmatched = rows.filter(r => r.match_source === 'unmatched' || r.selected_department_id == null).length;
    const matched = rows.filter(r => r.selected_department_id != null).length;
    return { auto, needs, unmatched, matched, total: rows.length };
  }, [rows]);

  // Track which departments are already selected in this import batch (excluding current row)
  // Also track departments already in the list
  const selectedDeptIds = useMemo(() => {
    const ids = new Set(existingDeptIds);
    rows.forEach(r => {
      if (r.selected_department_id != null) ids.add(r.selected_department_id);
    });
    return ids;
  }, [rows, existingDeptIds]);

  // Build a map of "sibling" departments (same acronym, different BU codes)
  // so when one FTB is selected, we can warn about the other
  const siblingMap = useMemo(() => {
    const byAcronym = {};
    departments.forEach(d => {
      const key = (d.acronym || d.official_name || '').toLowerCase().trim();
      if (!key) return;
      if (!byAcronym[key]) byAcronym[key] = [];
      byAcronym[key].push(d.department_id);
    });
    // Only keep groups with multiple departments (true duplicates)
    const map = {};
    Object.values(byAcronym).forEach(ids => {
      if (ids.length > 1) {
        ids.forEach(id => { map[id] = ids.filter(x => x !== id); });
      }
    });
    return map;
  }, [departments]);

  const updateRow = (i, patch) => {
    setRows(prev => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const selectDept = (i, deptId) => {
    if (deptId == null || deptId === '') {
      updateRow(i, { selected_department_id: null, match_source: 'unmatched', match_confidence: null });
      return;
    }
    const id = Number(deptId);
    const dept = departments.find(d => d.department_id === id);
    // If chosen from candidate list, use that confidence; otherwise mark manual.
    const fromCandidate = rows[i].candidates.find(c => c.department.department_id === id);
    updateRow(i, {
      selected_department_id: id,
      match_confidence: fromCandidate ? fromCandidate.confidence : null,
      match_source: fromCandidate ? (fromCandidate.confidence >= 0.85 ? 'auto' : 'manual') : 'manual',
      _manualDept: dept,
    });
  };

  // Check if a department is already selected elsewhere (excluding current row)
  const isDeptSelectedElsewhere = (deptId, currentRowIdx) => {
    if (deptId == null) return false;
    // Already in list before import
    if (existingDeptIds.includes(deptId)) return true;
    // Already selected in another row
    return rows.some((r, idx) => idx !== currentRowIdx && r.selected_department_id === deptId);
  };

  return (
    <>
      <div className="al-modal-body al-review-body">
        <div className="al-review-summary">
          <span className="al-chip al-chip-good"><Check size={12} /> {counts.matched} matched</span>
          {counts.needs > 0 && <span className="al-chip al-chip-warn">{counts.needs} need confirmation</span>}
          {counts.unmatched > 0 && <span className="al-chip al-chip-bad">{counts.unmatched} unmatched</span>}
        </div>

        <div className="al-review-table-wrap">
          <table className="al-review-table">
            <thead>
              <tr>
                <th>Salesforce account</th>
                <th>Matched department</th>
                <th>Confidence</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const dept = r._manualDept
                  || departments.find(d => d.department_id === r.selected_department_id);
                const isDuplicate = isDeptSelectedElsewhere(r.selected_department_id, i);
                const hasSiblings = r.selected_department_id && siblingMap[r.selected_department_id]?.length > 0;
                // Filter candidates to exclude already-selected departments (but keep current selection)
                const availableCandidates = r.candidates.filter(c =>
                  c.department.department_id === r.selected_department_id ||
                  !isDeptSelectedElsewhere(c.department.department_id, i)
                );
                // Filter "pick any" to exclude already-selected (allow current selection)
                const availableDepts = departments.filter(d =>
                  d.department_id === r.selected_department_id ||
                  !isDeptSelectedElsewhere(d.department_id, i)
                ).slice(0, 200);
                return (
                  <tr key={i} className={`${r.selected_department_id == null ? 'is-unmatched' : ''} ${isDuplicate ? 'is-duplicate' : ''}`}>
                    <td>
                      <div className="al-cell-primary">{r.salesforce_account_name}</div>
                      {r.salesforce_territory && (
                        <div className="al-cell-secondary">{r.salesforce_territory}</div>
                      )}
                    </td>
                    <td>
                      <select
                        className={`al-select ${isDuplicate ? 'al-select-duplicate' : ''}`}
                        value={r.selected_department_id ?? ''}
                        onChange={(e) => selectDept(i, e.target.value)}
                      >
                        <option value="">— No match (keep raw name) —</option>
                        {availableCandidates.map(c => (
                          <option key={c.department.department_id} value={c.department.department_id}>
                            {c.department.acronym ? `${c.department.acronym} · ` : ''}
                            {c.department.official_name || c.department.common_name}
                            {c.department.bu_code ? ` (BU ${c.department.bu_code})` : ''}
                            {' '}— {Math.round(c.confidence * 100)}%
                          </option>
                        ))}
                        {dept && !r.candidates.some(c => c.department.department_id === dept.department_id) && (
                          <option value={dept.department_id}>
                            {dept.acronym ? `${dept.acronym} · ` : ''}{dept.official_name} (manual)
                          </option>
                        )}
                        <optgroup label="Pick any department">
                          {availableDepts.map(d => (
                            <option key={`all-${d.department_id}`} value={d.department_id}>
                              {d.acronym ? `${d.acronym} · ` : ''}{d.official_name}
                              {d.bu_code ? ` (BU ${d.bu_code})` : ''}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                      {dept?.bu_code && (
                        <div className="al-cell-secondary">BU {dept.bu_code}</div>
                      )}
                      {isDuplicate && (
                        <div className="al-cell-warning">
                          <AlertTriangle size={12} /> Already in list
                        </div>
                      )}
                      {hasSiblings && !isDuplicate && (
                        <div className="al-cell-info">
                          Multiple BU codes exist for this agency
                        </div>
                      )}
                    </td>
                    <td>
                      {r.match_confidence != null
                        ? <span className="al-confidence">{Math.round(r.match_confidence * 100)}%</span>
                        : <span className="al-muted">—</span>}
                    </td>
                    <td>
                      <span className={`al-source al-source-${r.match_source || 'unmatched'}`}>
                        {(r.match_source || 'unmatched').replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {existingCount > 0 && (
          <div className="al-merge-strategy">
            <div className="al-merge-label">This list already has {existingCount} member{existingCount === 1 ? '' : 's'}:</div>
            <label>
              <input type="radio" name="merge" checked={mergeStrategy === 'append'} onChange={() => setMergeStrategy('append')} />
              Append — keep existing, add only new
            </label>
            <label>
              <input type="radio" name="merge" checked={mergeStrategy === 'replace'} onChange={() => setMergeStrategy('replace')} />
              Replace — discard existing members
            </label>
          </div>
        )}

        {saveError && <div className="al-file-error"><AlertTriangle size={14} /> {saveError}</div>}
      </div>

      <footer className="al-modal-footer">
        <button className="al-btn" onClick={onBack}>Back</button>
        <div className="al-spacer" />
        <button className="al-btn al-btn-primary" onClick={onSave}>
          Save {counts.total} accounts
        </button>
      </footer>
    </>
  );
}

function AiUploadPane({ title, hint, accept, capture, onFile, busy, fileEntries, fileError }) {
  return (
    <div className="al-file-pane">
      <label className={`al-file-drop ${busy ? 'is-busy' : ''}`}>
        {busy ? <div className="al-spinner" /> : <Sparkles size={28} />}
        <div className="al-file-drop-title">{title}</div>
        <div className="al-file-drop-hint">{hint}</div>
        <input
          type="file"
          accept={accept}
          capture={capture}
          disabled={busy}
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
        />
        {busy && <div className="al-muted al-file-drop-hint">Extracting with Claude…</div>}
      </label>
      {fileError && <div className="al-file-error"><AlertTriangle size={14} /> {fileError}</div>}
      {fileEntries && fileEntries.length > 0 && (
        <div className="al-file-preview">
          <div className="al-file-preview-title">
            <Sparkles size={12} /> {fileEntries.length} accounts extracted — preview
          </div>
          <ul>
            {fileEntries.slice(0, 5).map((e, i) => (
              <li key={i}><span>{e.name}</span>{e.territory && <span className="al-muted"> · {e.territory}</span>}</li>
            ))}
            {fileEntries.length > 5 && <li className="al-muted">…and {fileEntries.length - 5} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
